import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Lock, Sparkles } from 'lucide-react';
import CollegeService from '../services/collegeService';
import CollegeCard from './CollegeCard';
import CollegeCardSkeleton from './CollegeCardSkeleton';
import { getCollegeId, getCollegeSlug } from '../utils/collegeListing';

const PREVIEW_LIMIT = 8;

const PublicCollegePreviewSection = () => {
  const navigate = useNavigate();
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadCollegePreview = async () => {
      try {
        const result = await CollegeService.getPublicCollegePreview({ limit: PREVIEW_LIMIT });
        if (!isMounted) {
          return;
        }

        setColleges(Array.isArray(result?.data) ? result.data : []);
        setError('');
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setColleges([]);
        setError(loadError.message || 'Unable to load colleges right now.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCollegePreview();

    return () => {
      isMounted = false;
    };
  }, []);

  const redirectToAuth = (pathname) => {
    navigate('/signup', {
      state: {
        from: {
          pathname
        }
      }
    });
  };

  const handleViewProfile = (college) => {
    const slug = getCollegeSlug(college);
    const collegeId = getCollegeId(college);
    const targetPath = slug ? `/college-profile/${slug}` : '/college-profile';
    const targetWithQuery = collegeId
      ? `${targetPath}?collegeId=${encodeURIComponent(collegeId)}`
      : targetPath;

    redirectToAuth(targetWithQuery);
  };

  const cards = useMemo(() => {
    return colleges.filter((college) => Boolean(getCollegeId(college))).slice(0, PREVIEW_LIMIT);
  }, [colleges]);

  return (
    <section className="py-14 lg:py-18">
      <div className="w-full overflow-hidden border-y border-white/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(255,250,244,0.98))] shadow-[0_24px_70px_-36px_rgba(15,23,42,0.28)]">
        <div className="border-b border-[#f3e5da] bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.10),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(243,111,33,0.12),_transparent_28%)] px-5 py-6 sm:px-7 sm:py-7 lg:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#f0dacf] bg-white/90 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#8f4cab] shadow-sm">
                <Sparkles className="h-3.5 w-3.5" />
                Featured Colleges
              </div>
              <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Explore top colleges before you sign up
              </h2>
              <p className="mt-2 text-sm leading-7 text-slate-600 sm:text-base">
                Browse campus snapshots, logos, and locations from selected colleges. Create an account to unlock the full college explorer and detailed profiles.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => redirectToAuth('/college-profile')}
                className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#9a51a0] to-[#f36f21] px-6 py-3.5 text-sm font-semibold text-white shadow-md transition-transform duration-300 hover:scale-[1.01]"
              >
                <span>Explore Colleges</span>
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/signin', { state: { from: { pathname: '/college-profile' } } })}
                className="inline-flex min-w-[240px] items-center justify-center gap-2 rounded-2xl border border-[#eadcf8] bg-white px-6 py-3.5 text-sm font-semibold text-[#6f43b2] transition-colors hover:bg-[#f8f3ff]"
              >
                <Lock className="h-4 w-4" />
                <span>Sign In to Continue</span>
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 py-6 sm:px-7 sm:py-7 lg:px-8">
          {error ? (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {loading ? (
            <>
              <div className="space-y-3 sm:hidden">
                {Array.from({ length: 4 }).map((_, index) => (
                  <CollegeCardSkeleton key={`public-college-preview-mobile-${index}`} compact />
                ))}
              </div>
              <div className="hidden grid-cols-1 gap-5 sm:grid sm:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: PREVIEW_LIMIT }).map((_, index) => (
                  <CollegeCardSkeleton key={`public-college-preview-${index}`} />
                ))}
              </div>
            </>
          ) : cards.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-slate-600">
              Colleges will appear here once preview data is available.
            </div>
          ) : (
            <>
              <div className="space-y-3 sm:hidden">
                {cards.map((college) => (
                  <CollegeCard
                    key={`compact-${getCollegeId(college)}`}
                    college={college}
                    onViewProfile={handleViewProfile}
                    compact
                  />
                ))}
              </div>

              <div className="hidden grid-cols-1 gap-5 sm:grid sm:grid-cols-2 xl:grid-cols-4">
                {cards.map((college) => (
                  <CollegeCard
                    key={getCollegeId(college)}
                    college={college}
                    onViewProfile={handleViewProfile}
                  />
                ))}
              </div>

              <div className="mt-7 flex justify-center">
                <button
                  type="button"
                  onClick={() => redirectToAuth('/college-profile')}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#eadcf8] bg-white px-6 py-3 text-sm font-semibold text-[#6f43b2] shadow-sm transition-colors hover:bg-[#f8f3ff]"
                >
                  Unlock Full College Explorer
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default PublicCollegePreviewSection;
